import React, { useCallback, useEffect, useMemo, useState } from 'react';
import budgetService from '../services/budgetService';
import { useAuth } from '../context/AuthContext';

const PERMISSIONS = {
  BUDGETS_READ: 'budgets:read',
  BUDGETS_EDIT_OWN_STORE: 'budgets:edit_own_store',
  BUDGETS_EDIT_ALL: 'budgets:edit_all',
  BUDGETS_SUBMIT: 'budgets:submit',
  BUDGETS_APPROVE: 'budgets:approve',
  BUDGETS_LOCK: 'budgets:lock',
  BUDGETS_REOPEN: 'budgets:reopen',
};

const WIZARD_STEPS = [
  {
    id: 'plan',
    title: 'Plan Setup',
    description: 'Create or select a budget plan for the fiscal year and scenario.',
  },
  {
    id: 'target',
    title: 'Set Yearly Target',
    description: 'Enter a yearly net revenue target for the selected store.',
  },
  {
    id: 'distribute',
    title: 'Distribute Targets',
    description: 'Generate monthly/weekly/daily derived targets from yearly values.',
  },
  {
    id: 'review',
    title: 'Review & Lifecycle',
    description: 'Review variance and move plan through submit/approve/lock lifecycle.',
  },
];

const defaultPlanForm = {
  name: '',
  fiscalYear: new Date().getFullYear(),
  scenario: 'baseline',
  canonicalPeriodType: 'monthly',
  weekDefinition: 'iso',
  fiscalWeekStartsOn: 1,
  notes: '',
};

const statusPillClass = {
  draft: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-indigo-100 text-indigo-800',
  locked: 'bg-gray-200 text-gray-800',
};

const ConfirmDialog = ({ open, title, message, confirmLabel, onCancel, onConfirm, pending }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto p-5 space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="px-4 py-2 border rounded-md" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-primary text-white rounded-md disabled:opacity-60"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const BudgetManagementPage = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const [planForm, setPlanForm] = useState(defaultPlanForm);
  const [targetAmount, setTargetAmount] = useState('');
  const [yearlyStart, setYearlyStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [yearlyEnd, setYearlyEnd] = useState(`${new Date().getFullYear()}-12-31`);

  const [targets, setTargets] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [reportPeriodType, setReportPeriodType] = useState('monthly');
  const [activeTab, setActiveTab] = useState('targets');

  const [wizardMode, setWizardMode] = useState(true);
  const [wizardStep, setWizardStep] = useState(0);

  const [pendingAction, setPendingAction] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [confirmState, setConfirmState] = useState({
    open: false,
    type: '',
    payload: null,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
  });

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan._id) === String(selectedPlanId)) || null,
    [plans, selectedPlanId]
  );
  const selectedPlanStatus = selectedPlan?.status || null;
  const hasSelectedPlan = Boolean(selectedPlan);

  const hasPermission = (permission) => Boolean(user?.permissions?.includes(permission));

  const canCreatePlan = hasPermission(PERMISSIONS.BUDGETS_EDIT_ALL);
  const canEditTargets =
    hasPermission(PERMISSIONS.BUDGETS_EDIT_ALL) || hasPermission(PERMISSIONS.BUDGETS_EDIT_OWN_STORE);
  const canSubmit = hasPermission(PERMISSIONS.BUDGETS_SUBMIT) || hasPermission(PERMISSIONS.BUDGETS_EDIT_ALL);
  const canApprove = hasPermission(PERMISSIONS.BUDGETS_APPROVE);
  const canLock = hasPermission(PERMISSIONS.BUDGETS_LOCK);
  const canReopen = hasPermission(PERMISSIONS.BUDGETS_REOPEN);
  const canDistribute = hasPermission(PERMISSIONS.BUDGETS_EDIT_ALL);
  const isDraftPlan = selectedPlan?.status === 'draft';
  const isReadOnlyPlan = Boolean(selectedPlan) && !isDraftPlan;

  const hasYearlyTarget = useMemo(
    () => targets.some((row) => row.periodType === 'yearly'),
    [targets]
  );
  const hasDerivedTargets = useMemo(
    () => targets.some((row) => row.source === 'derived' && row.periodType !== 'yearly'),
    [targets]
  );

  const getWorkflowStepState = (index) => {
    if (index === 0) return selectedPlan ? 'complete' : 'pending';
    if (!selectedPlan) return 'pending';
    if (isReadOnlyPlan && (index === 1 || index === 2)) return 'na';
    if (index === 1) return hasYearlyTarget ? 'complete' : 'pending';
    if (index === 2) return hasDerivedTargets ? 'complete' : 'pending';
    if (index === 3) return ['submitted', 'approved', 'locked'].includes(selectedPlan?.status) ? 'complete' : 'pending';
    return 'pending';
  };

  const workflowSteps = WIZARD_STEPS.map((step, index) => ({
    label: step.title,
    state: getWorkflowStepState(index),
  }));

  const canGoNext = useMemo(() => {
    if (isReadOnlyPlan) return wizardStep < WIZARD_STEPS.length - 1;
    if (wizardStep === 0) return Boolean(selectedPlan);
    if (wizardStep === 1) return hasYearlyTarget;
    if (wizardStep === 2) return hasDerivedTargets;
    return true;
  }, [wizardStep, selectedPlan, hasYearlyTarget, hasDerivedTargets, isReadOnlyPlan]);

  const lifecycleButtons = [
    {
      action: 'submit',
      label: 'Submit',
      visible: selectedPlan?.status === 'draft' && canSubmit,
    },
    {
      action: 'reject',
      label: 'Reject to Draft',
      visible: selectedPlan?.status === 'submitted' && canApprove,
    },
    {
      action: 'approve',
      label: 'Approve',
      visible: selectedPlan?.status === 'submitted' && canApprove,
    },
    {
      action: 'lock',
      label: 'Lock',
      visible: selectedPlan?.status === 'approved' && canLock,
    },
    {
      action: 'reopen',
      label: 'Reopen to Draft',
      visible: ['approved', 'locked'].includes(selectedPlan?.status) && canReopen,
    },
  ].filter((button) => button.visible);

  const refreshPlans = useCallback(async () => {
    const { data } = await budgetService.getPlans();
    const nextPlans = data.data || [];
    setPlans(nextPlans);
    if (!selectedPlanId && nextPlans.length) {
      setSelectedPlanId(nextPlans[0]._id);
    }
  }, [selectedPlanId]);

  const refreshStores = useCallback(async () => {
    const { data } = await budgetService.getStores();
    const nextStores = data.data || [];
    setStores(nextStores);
    if (!selectedStoreId && nextStores.length) {
      setSelectedStoreId(nextStores[0]._id);
    }
  }, [selectedStoreId]);

  const refreshTargets = useCallback(async (planId, storeId) => {
    if (!planId || !storeId) return;
    const { data } = await budgetService.getTargets(planId, { storeId });
    setTargets(data.data || []);
  }, []);

  const refreshReport = useCallback(async (planId, storeId, periodType) => {
    if (!planId || !storeId) return;
    const { data } = await budgetService.getReport({ planId, storeId, periodType });
    setReportRows(data.data || []);
  }, []);

  const refreshAuditLogs = useCallback(async (planId) => {
    if (!planId) return;
    const { data } = await budgetService.getAuditLogs({ planId, limit: 100 });
    setAuditLogs(data.data || []);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([refreshPlans(), refreshStores()]);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load budgets.');
    } finally {
      setLoading(false);
    }
  }, [refreshPlans, refreshStores]);

  const withAction = async (key, work, successMessage) => {
    setPendingAction(key);
    setError('');
    setSuccess('');
    try {
      await work();
      if (successMessage) setSuccess(successMessage);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Action failed.');
    } finally {
      setPendingAction('');
    }
  };

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!selectedPlanId || !selectedStoreId) return;
    const run = async () => {
      try {
        await Promise.all([
          refreshTargets(selectedPlanId, selectedStoreId),
          refreshReport(selectedPlanId, selectedStoreId, reportPeriodType),
        ]);
      } catch (err) {
        setError(err?.response?.data?.message || err.message || 'Failed to load budget detail.');
      }
    };
    run();
  }, [selectedPlanId, selectedStoreId, reportPeriodType, refreshReport, refreshTargets]);

  useEffect(() => {
    if (activeTab !== 'audit' || !selectedPlanId) return;
    const loadLogs = async () => {
      try {
        await refreshAuditLogs(selectedPlanId);
      } catch (err) {
        setError(err?.response?.data?.message || err.message || 'Failed to load audit logs.');
      }
    };
    loadLogs();
  }, [activeTab, selectedPlanId, refreshAuditLogs]);

  useEffect(() => {
    if (!wizardMode) return;
    if (!hasSelectedPlan) {
      if (wizardStep !== 0) setWizardStep(0);
      return;
    }
    if (selectedPlanStatus !== 'draft' && wizardStep !== 3) {
      setWizardStep(3);
    }
  }, [wizardMode, hasSelectedPlan, selectedPlanStatus, wizardStep]);

  const openConfirmation = (type, payload, title, message, confirmLabel = 'Confirm') => {
    setConfirmState({
      open: true,
      type,
      payload,
      title,
      message,
      confirmLabel,
    });
  };

  const closeConfirmation = () => {
    setConfirmState({ open: false, type: '', payload: null, title: '', message: '', confirmLabel: 'Confirm' });
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!canCreatePlan) return;
    await withAction(
      'create-plan',
      async () => {
        const { data } = await budgetService.createPlan(planForm);
        setPlanForm(defaultPlanForm);
        await refreshPlans();
        if (data.data?._id) setSelectedPlanId(data.data._id);
      },
      'Budget plan created.'
    );
  };

  const executeLifecycleAction = async (action) => {
    if (!selectedPlanId) return;
    await withAction(
      `lifecycle-${action}`,
      async () => {
        if (action === 'submit') await budgetService.submitPlan(selectedPlanId);
        if (action === 'reject') await budgetService.rejectPlan(selectedPlanId);
        if (action === 'approve') await budgetService.approvePlan(selectedPlanId);
        if (action === 'lock') await budgetService.lockPlan(selectedPlanId);
        if (action === 'reopen') await budgetService.reopenPlan(selectedPlanId);
        await refreshPlans();
      },
      `Plan ${action} successful.`
    );
  };

  const handleStatusAction = (action) => {
    if (!selectedPlanId) return;

    if (action === 'lock') {
      openConfirmation(
        'lifecycle',
        { action },
        'Lock this plan?',
        'Locking prevents further target changes until reopened.',
        'Lock Plan'
      );
      return;
    }

    if (action === 'reopen') {
      openConfirmation(
        'lifecycle',
        { action },
        'Reopen plan to draft?',
        'This resets approval state and makes targets editable again.',
        'Reopen to Draft'
      );
      return;
    }

    if (action === 'reject') {
      openConfirmation(
        'lifecycle',
        { action },
        'Reject plan back to draft?',
        'The plan will return to draft so targets can be corrected before submission.',
        'Reject to Draft'
      );
      return;
    }

    executeLifecycleAction(action);
  };

  const executeDistribute = async (periodType) => {
    if (!selectedPlanId || !selectedStoreId) return;
    await withAction(
      `distribute-${periodType}`,
      async () => {
        await budgetService.distributePlan(selectedPlanId, { periodType, storeIds: [selectedStoreId] });
        await Promise.all([
          refreshTargets(selectedPlanId, selectedStoreId),
          refreshReport(selectedPlanId, selectedStoreId, reportPeriodType),
        ]);
      },
      `Distribution to ${periodType} completed.`
    );
  };

  const handleDistribute = (periodType) => {
    openConfirmation(
      'distribute',
      { periodType },
      `Distribute to ${periodType}?`,
      `This will generate derived ${periodType} targets for the selected store. Existing values for matching periods may be overwritten.`,
      'Distribute'
    );
  };

  const handleConfirm = async () => {
    if (!confirmState.open) return;
    const { type, payload } = confirmState;

    if (type === 'lifecycle') {
      await executeLifecycleAction(payload.action);
    }
    if (type === 'distribute') {
      await executeDistribute(payload.periodType);
    }

    closeConfirmation();
  };

  const handleUpsertYearlyTarget = async (e) => {
    e.preventDefault();
    if (!selectedPlanId || !selectedStoreId || !canEditTargets) return;

    await withAction(
      'save-yearly-target',
      async () => {
        await budgetService.upsertTargets(selectedPlanId, {
          targets: [
            {
              storeId: selectedStoreId,
              metric: 'net_revenue',
              periodType: 'yearly',
              periodStart: yearlyStart,
              periodEnd: yearlyEnd,
              targetAmount: Number(targetAmount || 0),
              source: 'manual',
              isOverride: false,
            },
          ],
        });
        setTargetAmount('');
        await Promise.all([
          refreshTargets(selectedPlanId, selectedStoreId),
          refreshReport(selectedPlanId, selectedStoreId, reportPeriodType),
        ]);
      },
      'Yearly target saved.'
    );
  };

  const renderPlanSetup = () => (
    <div className="bg-white rounded-lg shadow p-4">
      <h2 className="text-lg font-semibold mb-3">Create Budget Plan</h2>
      {!canCreatePlan && (
        <p className="text-sm text-amber-700 mb-2">You can view plans but cannot create new plans with your current permissions.</p>
      )}
      <form className="grid grid-cols-1 md:grid-cols-7 gap-3" onSubmit={handleCreatePlan}>
        <input className="border rounded px-3 py-2" placeholder="Plan name" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} required disabled={!canCreatePlan || pendingAction === 'create-plan'} />
        <input className="border rounded px-3 py-2" type="number" value={planForm.fiscalYear} onChange={(e) => setPlanForm({ ...planForm, fiscalYear: Number(e.target.value) })} required disabled={!canCreatePlan || pendingAction === 'create-plan'} />
        <select className="border rounded px-3 py-2" value={planForm.scenario} onChange={(e) => setPlanForm({ ...planForm, scenario: e.target.value })} disabled={!canCreatePlan || pendingAction === 'create-plan'}>
          <option value="baseline">baseline</option>
          <option value="stretch">stretch</option>
          <option value="forecast">forecast</option>
        </select>
        <select className="border rounded px-3 py-2" value={planForm.canonicalPeriodType} onChange={(e) => setPlanForm({ ...planForm, canonicalPeriodType: e.target.value })} disabled={!canCreatePlan || pendingAction === 'create-plan'}>
          <option value="monthly">monthly</option>
          <option value="yearly">yearly</option>
        </select>
        <select className="border rounded px-3 py-2" value={planForm.weekDefinition} onChange={(e) => setPlanForm({ ...planForm, weekDefinition: e.target.value })} disabled={!canCreatePlan || pendingAction === 'create-plan'}>
          <option value="iso">ISO week (Mon start)</option>
          <option value="fiscal">Fiscal week</option>
        </select>
        <select className="border rounded px-3 py-2" value={planForm.fiscalWeekStartsOn} onChange={(e) => setPlanForm({ ...planForm, fiscalWeekStartsOn: Number(e.target.value) })} disabled={!canCreatePlan || planForm.weekDefinition !== 'fiscal' || pendingAction === 'create-plan'}>
          <option value={0}>Sunday start</option>
          <option value={1}>Monday start</option>
          <option value={2}>Tuesday start</option>
          <option value={3}>Wednesday start</option>
          <option value={4}>Thursday start</option>
          <option value={5}>Friday start</option>
          <option value={6}>Saturday start</option>
        </select>
        <button className="bg-primary text-white rounded px-4 py-2 disabled:opacity-60" type="submit" disabled={loading || !canCreatePlan || pendingAction === 'create-plan'}>
          {pendingAction === 'create-plan' ? 'Creating...' : 'Create'}
        </button>
      </form>
    </div>
  );

  const renderWorkspace = () => (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <h2 className="text-lg font-semibold">Plan Workspace</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select className="border rounded px-3 py-2" value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)}>
          {plans.map((plan) => (
            <option key={plan._id} value={plan._id}>{`${plan.name} v${plan.version || 1} (${plan.fiscalYear}) [${plan.status}]`}</option>
          ))}
        </select>
        <select className="border rounded px-3 py-2" value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)}>
          {stores.map((store) => (
            <option key={store._id} value={store._id}>{store.name}</option>
          ))}
        </select>
        <div className="text-sm text-gray-600 flex items-center">
          Use this selector before editing targets or running distribution.
        </div>
      </div>
      {selectedPlan && (
        <p className="text-sm text-gray-600">
          Selected: <span className="font-medium">{selectedPlan.name}</span> | Version: v{selectedPlan.version || 1} | Scenario: {selectedPlan.scenario} | Week Mode: {selectedPlan.weekDefinition || 'iso'} | Status:
          <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusPillClass[selectedPlan.status] || 'bg-gray-100 text-gray-700'}`}>
            {selectedPlan.status}
          </span>
        </p>
      )}
    </div>
  );

  const renderTargetEntry = () => (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <h2 className="text-lg font-semibold">Yearly Target Entry (Store)</h2>
      <form className="grid grid-cols-1 md:grid-cols-4 gap-3" onSubmit={handleUpsertYearlyTarget}>
        <input type="date" className="border rounded px-3 py-2" value={yearlyStart} onChange={(e) => setYearlyStart(e.target.value)} required disabled={!canEditTargets || selectedPlan?.status !== 'draft' || pendingAction === 'save-yearly-target'} />
        <input type="date" className="border rounded px-3 py-2" value={yearlyEnd} onChange={(e) => setYearlyEnd(e.target.value)} required disabled={!canEditTargets || selectedPlan?.status !== 'draft' || pendingAction === 'save-yearly-target'} />
        <input type="number" step="0.01" className="border rounded px-3 py-2" placeholder="Target amount" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} required disabled={!canEditTargets || selectedPlan?.status !== 'draft' || pendingAction === 'save-yearly-target'} />
        <button className="bg-primary text-white rounded px-4 py-2 disabled:opacity-60" type="submit" disabled={!canEditTargets || selectedPlan?.status !== 'draft' || pendingAction === 'save-yearly-target'}>
          {pendingAction === 'save-yearly-target' ? 'Saving...' : 'Save Yearly Target'}
        </button>
      </form>
      {selectedPlan?.status !== 'draft' && (
        <p className="text-xs text-gray-500">Target entry is available only while plan status is draft.</p>
      )}
      {hasYearlyTarget && <p className="text-sm text-green-700">Yearly target found. Continue to distribution.</p>}
    </div>
  );

  const renderDistribution = () => (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <h2 className="text-lg font-semibold">Distribute Targets</h2>
      <p className="text-sm text-gray-600">
        Generate lower-period targets from yearly values for the selected store.
      </p>
      <div className="flex gap-2 flex-wrap">
        {['monthly', 'weekly', 'daily'].map((periodType) => (
          <button
            key={periodType}
            type="button"
            className="border rounded px-3 py-2 disabled:opacity-60"
            onClick={() => handleDistribute(periodType)}
            disabled={!canDistribute || selectedPlan?.status !== 'draft' || pendingAction.startsWith('distribute-')}
          >
            {pendingAction === `distribute-${periodType}`
              ? 'Working...'
              : `Distribute to ${periodType.charAt(0).toUpperCase()}${periodType.slice(1)}`}
          </button>
        ))}
      </div>
      {selectedPlan?.status !== 'draft' && (
        <p className="text-xs text-gray-500">Distribution is available only while plan status is draft.</p>
      )}
      {hasDerivedTargets && <p className="text-sm text-green-700">Derived targets are ready. Continue to review and lifecycle.</p>}
    </div>
  );

  const renderReviewAndLifecycle = () => (
    <>
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <h2 className="text-lg font-semibold">Lifecycle Actions</h2>
        <div className="flex gap-2 flex-wrap">
          {lifecycleButtons.length === 0 && (
            <span className="text-sm text-gray-500">No lifecycle actions available for this status/permission.</span>
          )}
          {lifecycleButtons.map((button) => (
            <button
              key={button.action}
              type="button"
              className="border rounded px-3 py-2 disabled:opacity-60"
              onClick={() => handleStatusAction(button.action)}
              disabled={pendingAction.startsWith('lifecycle-')}
            >
              {pendingAction === `lifecycle-${button.action}` ? 'Working...' : button.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex gap-2">
            {[
              { id: 'targets', label: 'Targets' },
              { id: 'report', label: 'Variance Report' },
              { id: 'audit', label: 'Audit Log' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`px-3 py-2 rounded-md text-sm border ${
                  activeTab === tab.id ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'report' && (
            <select
              className="border rounded px-3 py-2 text-sm"
              value={reportPeriodType}
              onChange={(e) => setReportPeriodType(e.target.value)}
            >
              <option value="monthly">monthly</option>
              <option value="weekly">weekly</option>
              <option value="daily">daily</option>
              <option value="yearly">yearly</option>
            </select>
          )}
        </div>

        {activeTab === 'targets' && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Period</th>
                  <th className="py-2 pr-4">Start</th>
                  <th className="py-2 pr-4">End</th>
                  <th className="py-2 pr-4">Target</th>
                  <th className="py-2 pr-4">Source</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((row) => (
                  <tr key={row._id} className="border-b">
                    <td className="py-2 pr-4">{row.periodType}</td>
                    <td className="py-2 pr-4">{new Date(row.periodStart).toLocaleDateString()}</td>
                    <td className="py-2 pr-4">{new Date(row.periodEnd).toLocaleDateString()}</td>
                    <td className="py-2 pr-4">{Number(row.targetAmount || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4">{row.source}</td>
                  </tr>
                ))}
                {!targets.length && (
                  <tr>
                    <td className="py-3 text-gray-500" colSpan={5}>No targets yet for this plan/store.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'report' && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Period Start</th>
                  <th className="py-2 pr-4">Budget</th>
                  <th className="py-2 pr-4">Actual</th>
                  <th className="py-2 pr-4">Variance</th>
                  <th className="py-2 pr-4">Variance %</th>
                  <th className="py-2 pr-4">Achievement %</th>
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row) => (
                  <tr key={row.targetId} className="border-b">
                    <td className="py-2 pr-4">{new Date(row.periodStart).toLocaleDateString()}</td>
                    <td className="py-2 pr-4">{Number(row.budget || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4">{Number(row.actual || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4">{Number(row.variance || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4">{row.variancePct == null ? '-' : `${row.variancePct.toFixed(2)}%`}</td>
                    <td className="py-2 pr-4">{row.achievementPct == null ? '-' : `${row.achievementPct.toFixed(2)}%`}</td>
                  </tr>
                ))}
                {!reportRows.length && (
                  <tr>
                    <td className="py-3 text-gray-500" colSpan={6}>No report rows yet for selected period type.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Entity</th>
                  <th className="py-2 pr-4">Action</th>
                  <th className="py-2 pr-4">By</th>
                  <th className="py-2 pr-4">Reason</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((row) => (
                  <tr key={row._id} className="border-b">
                    <td className="py-2 pr-4">{new Date(row.performedAt || row.createdAt).toLocaleString()}</td>
                    <td className="py-2 pr-4">{row.entityType}</td>
                    <td className="py-2 pr-4">{row.action}</td>
                    <td className="py-2 pr-4">{row.performedBy?.name || row.performedBy?.email || '-'}</td>
                    <td className="py-2 pr-4">{row.reason || '-'}</td>
                  </tr>
                ))}
                {!auditLogs.length && (
                  <tr>
                    <td className="py-3 text-gray-500" colSpan={5}>No audit entries for this plan yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  const renderStepContent = () => {
    if (isReadOnlyPlan) return renderReviewAndLifecycle();
    if (wizardStep === 0) return renderPlanSetup();
    if (wizardStep === 1) return renderTargetEntry();
    if (wizardStep === 2) return renderDistribution();
    return renderReviewAndLifecycle();
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Budget Management</h1>
        <p className="text-sm text-gray-600 mt-1">Plan store targets, distribute to period levels, and track variance against actuals.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Guided Mode</h2>
            <p className="text-sm text-gray-600">
              {wizardMode
                ? isReadOnlyPlan
                  ? `Review & Lifecycle: This plan is read-only while status is ${selectedPlan?.status}.`
                  : `${WIZARD_STEPS[wizardStep].title}: ${WIZARD_STEPS[wizardStep].description}`
                : 'Guided mode is off. You can use full-page advanced view.'}
            </p>
          </div>
          <button
            type="button"
            className="border rounded px-3 py-2 text-sm"
            onClick={() => setWizardMode((prev) => !prev)}
          >
            {wizardMode ? 'Switch to Full View' : 'Switch to Guided View'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {workflowSteps.map((step, index) => (
            <div
              key={step.label}
              className={`border rounded-md p-3 ${wizardMode && wizardStep === index ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}
            >
              <div className="text-xs text-gray-500">Step {index + 1}</div>
              <div className="font-medium text-gray-800">{step.label}</div>
              <div className={`text-xs mt-1 ${step.state === 'complete' ? 'text-green-700' : step.state === 'na' ? 'text-amber-700' : 'text-gray-500'}`}>
                {step.state === 'complete' ? 'Complete' : step.state === 'na' ? 'N/A (Read-only)' : 'Pending'}
              </div>
            </div>
          ))}
        </div>

        {wizardMode && (
          <div className="flex justify-between items-center gap-3">
            <button
              type="button"
              className="border rounded px-3 py-2 disabled:opacity-60"
              onClick={() => setWizardStep((prev) => Math.max(prev - 1, 0))}
              disabled={wizardStep === 0 || Boolean(pendingAction) || isReadOnlyPlan}
            >
              Back
            </button>
            <button
              type="button"
              className="bg-primary text-white rounded px-3 py-2 disabled:opacity-60"
              onClick={() => setWizardStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1))}
              disabled={wizardStep >= WIZARD_STEPS.length - 1 || !canGoNext || Boolean(pendingAction) || isReadOnlyPlan}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {error && <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-md">{error}</div>}
      {success && <div className="bg-green-100 border border-green-300 text-green-700 px-4 py-3 rounded-md">{success}</div>}

      {renderWorkspace()}

      {wizardMode ? (
        renderStepContent()
      ) : (
        <>
          {renderPlanSetup()}
          {renderTargetEntry()}
          {renderDistribution()}
          {renderReviewAndLifecycle()}
        </>
      )}

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        onCancel={closeConfirmation}
        onConfirm={handleConfirm}
        pending={Boolean(pendingAction)}
      />
    </div>
  );
};

export default BudgetManagementPage;
